import React, { useRef, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday } from 'date-fns';
import EthiopianCalendar from '../../../../util/ethiopia/ethiopianCalendar';
import BookItOrCreate from '../bookItOrCreate';
import BookingTypeList from './bookingTypeList';
import { colorNameToHex } from '../../../../util/colorMap';
import { QRCodeCanvas } from 'qrcode.react';

const RenderDays = ({ month, showEventDetailsInside, mHeight }) => {
    const bookItOrCreateRef = useRef(null);  
    // Calculate the range of dates to display (include dates from previous and next months to fill the grid)
    const startDate = startOfMonth(month); // First day of the current month
    const endDate = endOfMonth(month);     // Last day of the current month
    const startOfWeekDate = startOfWeek(startDate); // First day of the first week
    const endOfWeekDate = endOfWeek(endDate);       // Last day of the last week

    const days = eachDayOfInterval({ start: startOfWeekDate, end: endOfWeekDate });

    return (
        <Grid container>
            {/* Weekday Headers */}
            <Grid container>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                    <Grid item xs={12 / 7} key={index} align="center">
                        <Typography
                            variant="body2"
                            style={{
                                color: colorNameToHex.DarkGrayBlue,
                                fontWeight: 'bold',
                                fontSize: '20px',
                                fontFamily: '"Inter", "Noto Sans", "Segoe UI", "Roboto", sans-serif',
                            }}
                        >
                            {day}
                        </Typography>
                    </Grid>
                ))}
            </Grid>

            {/* Render Days */}
            {days.map((day, index) => {
                const isCurrentMonth = isSameMonth(day, month);
                const isCurrentDay = isToday(day);
                const gregorianDate = day.getDate();


                // 1. Convert to Ethiopian date
                const ethiopianDate = EthiopianCalendar.getEthiopianDate(
                    day.getFullYear(),
                    day.getMonth() + 1,
                    day.getDate()
                );
 
                return (
                    <Grid
                        item
                        key={index}
                        xs={12 / 7}
                        className={`calendar-cell row-${Math.floor(index / 7)} col-${index % 7}`}
                        style={{
                            position: 'relative',
                            background: isCurrentDay ? '#ffeeba' : isCurrentMonth ? '#fff' : '#f9f9f9',
                            opacity: isCurrentMonth ? 1 : 0.5,
                            cursor: 'pointer',
                            height: mHeight,
                            boxSizing: 'border-box',

                            // Apply borders only to avoid overlap
                            borderRight: `.5px solid ${colorNameToHex.DarkGrayBlue}`,
                            borderBottom: `.5px solid ${colorNameToHex.DarkGrayBlue}`,
                            borderTop: index < 7 ? `.5px solid ${colorNameToHex.DarkGrayBlue}` : undefined,
                            borderLeft: index % 7 === 0 ? `.5px solid ${colorNameToHex.DarkGrayBlue}` : undefined,
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            // bookItOrCreateRef.current?.('book', '', day, month);
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = isCurrentDay
                                ? '#ffd966'
                                : isCurrentMonth
                                    ? '#f0f4ff'
                                    : '#f3f3f3';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = isCurrentDay
                                ? '#ffeeba'
                                : isCurrentMonth
                                    ? '#fff'
                                    : '#f9f9f9';
                        }}
                    >

                        {isCurrentMonth ? (

                            <div style={{ height: '100%', width: '100%', position: 'relative' }}>
                            {/* Gregorian Date (Top-left) */}
                            <Typography
                                variant="body2"
                                sx={{
                                    position: 'absolute',
                                    top: 4,
                                    left: 4,
                                    fontSize: '20px',
                                    fontFamily: '"Inter", "Noto Sans", "Segoe UI", "Roboto", sans-serif',
                                    color: colorNameToHex.DarkGrayBlue,
                                    lineHeight: 1,
                                }}
                            >
                                {gregorianDate}
                            </Typography>

                            {/* Ethiopian Date (Top-right) */}
                            <Box
                                sx={{
                                        position: 'absolute',
                                    right: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    fontFamily: '"Inter", "Noto Sans", "Segoe UI", "Roboto", sans-serif',
                                    lineHeight: 1,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        top: 4,
                                        fontSize: '20px',
                                        fontWeight: ethiopianDate.ethiopianDay === 1 ? 'bold' : 'normal',
                                        color: colorNameToHex.DarkGrayBlue,
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1,
                                    }}
                                >
                                    {ethiopianDate.ethiopianDay === 1 ? (
                                        <>
                                                <span style={{ color: colorNameToHex.DeepSkyBlue }}>
                                                {EthiopianCalendar.toEthiopianMonth(ethiopianDate.ethiopianMonth)}
                                            </span>
                                            <span style={{ marginLeft: 4 }}>
                                                    - {ethiopianDate.ethiopianDay} <span style={{ fontSize: '24px', color: colorNameToHex.DarkGrayBlue }}>
                                                    {EthiopianCalendar.toGeezNumber(ethiopianDate.ethiopianDay)}
                                                </span>
                                            </span>
                                        </>
                                    ) : (
                                            <>
                                                    {ethiopianDate.ethiopianDay} <span style={{ fontSize: '24px', color: colorNameToHex.DarkGrayBlue }}>
                                                    {EthiopianCalendar.toGeezNumber(ethiopianDate.ethiopianDay)}
                                                </span>
                                        </>
                                    )}
                                </Typography>
                            </Box>

                            {/* Event Details (Centered) */}
                            {showEventDetailsInside && (
                                <div style={{ marginTop: '1px', textAlign: 'center' }}>
                                    <BookingTypeList
                                        date={day}
                                        showEventDetails={showEventDetailsInside}
                                        showEventMarker={false}
                                        day={day}
                                        month={month}
                                    />
                                </div>
                            )}
                        </div>
                        ) : (                            
                                // All other off-month slots remain blank
                                <Box sx={{ height: '100%', width: '100%' }} />
                         )}
                    </Grid>
                ); 
            })}

            {/* Dialog Handler */}
            {/* <BookItOrCreate triggerDialog={(handler) => (bookItOrCreateRef.current = handler)} /> */}
        </Grid>
    );
};

export default RenderDays;
